// pages/roster-management/bulk-upload/Upload.tsx
import React, { useState } from 'react';
import type Locale from '@whizz/api-client/dist/@types/model/Locale';
import type { RosterManagementStudentListItem, TagsGet } from '@whizz/api-model';
import { Translate, useI18n } from '@whizz/react-i18n';
import exportFromJson from 'export-from-json';
import { Button } from '../../../../components';
import RosterManagementLayout from '../../../../layouts/RosterManagementLayout';
import { useSuccessErrorModal } from '../../../../hooks/useSuccessErrorModal';
import { GLOBAL_ICONS, genders } from '../../../../util/vars';
import { useCleverIntegration } from '../../../../hooks/useCleverIntegration';
import { repository } from '@whizz/api-client';
import { RosterManagementStudentActionEnum } from '@whizz/api-model';
import { DateTime } from 'luxon';
import { range, sortBy } from 'lodash';
import StudentUploadPage from './StudentUploadPage';
import whizzyIcon from '../../../../assets/icon-default-whizzy.svg';
import studentsIcon from '../../../../assets/Cluedo-Nudges-Mexican-Wave.svg';
import techersIcon from '../../../../assets/Cluedo-Nudges-Teachers-Admin.svg';
import parentsIcon from '../../../../assets/Cluedo-Nudges-Teachers-Magnify.svg';
import styled, { createGlobalStyle } from 'styled-components';
import Modal, { ModalFooter } from '../../../../components/modal/Modal';
import { useIdentity } from '@whizz/react-identity';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';


type Panel = 'students' | 'teachers' | 'parents' | null;


const BulkUploadOverrides = createGlobalStyle`
  /* Only affect the bulk-upload page */
  .bulk-upload-page .bg-white.rounded-default.shadow {
    background: transparent !important;
    box-shadow: none !important;
  }
`;

const studentExampleRowsV2 = [
  {
    upn: 'D1753058',
    firstName: 'Olivia',
    lastName: 'Jones',
    dob: '10/08/2016',
    yearGroup: 'Y2',
    workingAge: '7',
    gender: 'F',
    password: '',
    username: 'oliviaj',
    license: 'Y',
    className: 'Oak',
    tag: 'SEN',
    aboveBelowAssessmentDelta: 'Above',
    assessmentDelta: '2 Years',
  },
  {
    upn: 'D1753059',
    firstName: 'Dexter',
    lastName: 'Smith',
    dob: '07/10/2017',
    yearGroup: 'Y3',
    workingAge: '8',
    gender: 'M',
    password: '',
    username: 'dexters',
    license: 'Y',
    className: 'Acorn,Oak',
    tag: '',
    aboveBelowAssessmentDelta: 'Below',
    assessmentDelta: '4 Years',
  },
];

const teacherExampleRows = [
  {
    title: 'Mr',
    jobtype: 'Teacher',
    firstName: 'Amir',
    lastName: 'Khan',
    email: 'amir.khan@example.com',
    recoveryEmail: '',
    phone: '0207 123 4567',
    classes: '101;102',
    admin: 'Y',
  },
  {
    title: 'Ms',
    jobtype: 'Teacher',
    firstName: 'Rachel',
    lastName: 'Nguyen',
    email: 'rachel.nguyen@example.com',
    recoveryEmail: 'rachel.backup@example.com',
    phone: '07900 123456',
    classes: '103',
    admin: '',
  },
];

const parentExampleRows = [
  {
    upn: 'QA68686961133',
    studentName: 'Ben Smith',
    g1Name: 'bendad',
    e1: 'smith@example.com',
    g2Name: 'benmom',
    e2: 'smithmom@example.com',
    g3Name: '',
    e3: '',
  },
  {
    upn: 'D1753059',
    studentName: 'Olivia Jones',
    g1Name: 'terry jones',
    e1: 'ojones.parent@example.com',
    g2Name: '',
    e2: '',
    g3Name: '',
    e3: '',
  },
];
const STUDENT_TEMPLATE_HEADERS = [
  'Archive',
  'UPN',
  'First Name',
  'Last Name',
  'Date Of Birth',
  'Year Group',
  'Gender',
  'Password',
  'Username',
  'Activate Student',
  'Class',
  'Tags',
  'Working Age',
  'Assessment Age',
];

const STUDENT_EXPORT_HEADERS = [
  'Archive',
  'UPN',
  'First Name',
  'Last Name',
  'Date Of Birth',
  'Year Group',
  'Gender',
  'Password',
  'Username',
  'Activate Student',
  'Class',
  'Tags',
  'Working Age',
  'Assessment Age',
];


const TEACHER_TEMPLATE_HEADERS = [
  'Job Type',
  'Title',
  'First Name',
  'Last Name',
  'Email Address',
  'Recovery Email Address',
  'Phone',
  'Classes',
  'Admin',
];

const PARENT_TEMPLATE_HEADERS = [
  'UPN',
  'Student Name',
  'Parent/Guardian 1 Name',
  'Email 1',
  'Parent/Guardian 2 Name',
  'Email 2',
  'Parent/Guardian 3 Name',
  'Email 3',
];



// --- Blank Student CSV template (headers match StudentUploadPage importer) ---
function downloadBlankStudentCsv(translate: (key: string) => string) {
  const BOM = '\ufeff';
  const csv = STUDENT_TEMPLATE_HEADERS.join(',') + '\r\n';
  const blob = new Blob([ BOM + csv ], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  // Localised filename with fallback
  const fileName =
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_BLANK_STUDENTS_FILENAME') ||
    'Maths_Whizz_Student_Blank_Template.csv';

  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadBlankTeacherCsv(translate: (key: string) => string) {
  const BOM = '\ufeff';
  const csv = TEACHER_TEMPLATE_HEADERS.join(',') + '\r\n';
  const blob = new Blob([ BOM + csv ], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  const fileName =
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_BLANK_TEACHERS_FILENAME') ||
    'Maths_Whizz_Staff_Blank_Template.csv';

  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadBlankParentsCsv(translate: (key: string) => string) {
  const BOM = '\ufeff';
  const csv = PARENT_TEMPLATE_HEADERS.join(',') + '\r\n';
  const blob = new Blob([ BOM + csv ], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  const fileName =
    translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_BLANK_PARENTS_FILENAME') ||
    'Maths_Whizz_Parents_Blank_Template.csv';

  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const Guidelines = styled('div')`
  th,
  td {
    text-align: left;
    border: 1px solid #b7b7b7;
    padding: 0.2rem;
    font-size: 0.75rem;

    @media (min-width: 1024px) {
      padding: 0.3rem;
      font-size: 1rem;
    }
  }
  li {
    & span {
      font-weight: bold;
    }
  }
`;

const Section = styled.div`
  width: 100%;
  border: 1px solid #e6e6e6;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
`;

const SectionHeader = styled.button<{ $open: boolean }>`
  width: 100%;
  text-align: left;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(p) => (p.$open ? '#f9fafb' : '#fff')};
  cursor: pointer;
  border: 0;
`;

const Title = styled.div`
  display: flex;
  gap: 12px;
  align-items: baseline;
  font-size: 16px;

  .pill {
    color: #67b500; /* "positive" green */
    font-weight: 800;
    letter-spacing: 0.3px;
  }
  .sub {
    color: #555;
    font-weight: 500;
  }
`;

const SectionBody = styled.div`
  padding: 18px 20px 22px 20px;
  border-top: 1px solid #eee;
`;

const FullBleed = styled.div`
  position: relative;
  padding: 0px 50px 0px 50px;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  width: 100vw;
`;

const TranslateWithSpan = ({ i18nKey }: { i18nKey: string }) => (
  <Translate i18nKey={i18nKey}>
    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
    {/* @ts-ignore */}
    <span />
  </Translate>
);

const Upload2 = (): JSX.Element => {
  const { locale, translate } = useI18n();
  const [ successErrorModal, toggleMessageType ] = useSuccessErrorModal();
  const [ isClever ] = useCleverIntegration();
  const [ openPanel, setOpenPanel ] = useState<Panel>(null); // default
  const [ showStudentUploader, setShowStudentUploader ] = useState(false);
  const [ showTeacherUploader, setShowTeacherUploader ] = useState(false);
  const [ showParentUploader, setShowParentUploader ] = useState(false);

  const [ studentsDirty, setStudentsDirty ] = useState(false);
  const [ teachersDirty, setTeachersDirty ] = useState(false);
  const [ parentsDirty, setParentsDirty ] = useState(false);

  // === Confirm modal state & helpers (INSIDE component) ===
  const [ confirmOpen, setConfirmOpen ] = React.useState(false);
  const [ confirmTitle, setConfirmTitle ] = React.useState<string>('');
  const [ confirmBody, setConfirmBody ] = React.useState<React.ReactNode>(null);
  const [ confirmDestructive, setConfirmDestructive ] = React.useState(false);
  const [ confirmOkText, setConfirmOkText ] = React.useState('OK');
  const [ confirmCancelText, setConfirmCancelText ] = React.useState('Cancel');
  const confirmResolveRef = React.useRef<null | ((v: boolean) => void)>(null);

  //bulk uploads
  const { me } = useIdentity();
  const userCountry = (me as Teacher).country;
  const canAddOrEditStaff = userCountry ? ![ 'nz' ].includes(userCountry) : false;
  const studentsRef = React.useRef<HTMLDivElement>(null);
  const teachersRef = React.useRef<HTMLDivElement>(null);
  const parentsRef  = React.useRef<HTMLDivElement>(null);
  const localeId = (
    (me as unknown as { locale?: { id?: string | null } })?.locale?.id ??
  'en-gb'
  ).toLowerCase();
  const isMonthFirstDobLocale = localeId === 'en-us' || localeId === 'en-nz';

  const TOP_OFFSET = 12;
  const STICKY_OFFSET = 12;

  const studentExamples = [
    {
      firstName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.FIRST_NAME'),
      lastName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LAST_NAME'),
      upn: <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN'}/>,
      yearGroup: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.YEAR_GROUP'),
      gender: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER'),
      password: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.PASSWORD'),
      activate: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN'),
      dob: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.DOB'),
      classes: [ 'EXAMPLE1.CLASS' ],
      tags: [ 'SEN' ],
    },
    {
      firstName: 'EXAMPLE2.FIRST_NAME',
      lastName: 'EXAMPLE2.LAST_NAME',
      upn: 'EXAMPLE2.UPN',
      yearGroup: 'EXAMPLE2.YEAR_GROUP',
      gender: 'EXAMPLE2.GENDER',
      password: 'EXAMPLE2.PASSWORD',
      activate: 'EXAMPLE2.ACTIVATE',
      dob: 'EXAMPLE2.DOB',
      classes: [ 'EXAMPLE2.CLASS' ],
      tags: [ '' ],
    },
  ];

  React.useEffect(() => {
    if (!openPanel) return;

    // 1 frame to render expand, then a microtask for any layout shifts
    const raf = requestAnimationFrame(() => {
      setTimeout(() => {
        scrollToPanel(openPanel);
      }, 0);
    });

    return () => cancelAnimationFrame(raf);
  }, [ openPanel, showStudentUploader, showTeacherUploader, showParentUploader ]);


  const scrollToPanel = (panel: Panel) => {
    if (!panel) return;

    const map = { students: studentsRef, teachers: teachersRef, parents: parentsRef } as const;
    const el = map[panel]?.current;
    if (!el) return;

    const doScroll = () => {
      const rectTop = el.getBoundingClientRect().top;
      const absoluteTop = window.scrollY + rectTop;
      const targetY = Math.max(absoluteTop - STICKY_OFFSET, 0);
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    };

    // run a few times to catch height shifts after expand/render
    requestAnimationFrame(() => {
      doScroll();
      setTimeout(doScroll, 60);
      setTimeout(doScroll, 180);
    });
  };


  const askConfirm = React.useCallback(
    (
      body: React.ReactNode,
      opts?: { title?: string; destructive?: boolean; confirmText?: string; cancelText?: string },
    ): Promise<boolean> => {
      setConfirmBody(body);
      setConfirmTitle(opts?.title ?? '');
      setConfirmDestructive(!!opts?.destructive);
      setConfirmOkText(opts?.confirmText ?? translate('pages.components.BUTTONS.OK'));
      setConfirmCancelText(opts?.cancelText ?? translate('pages.components.BUTTONS.CANCEL'));
      setConfirmOpen(true);
      return new Promise<boolean>((resolve) => {
        confirmResolveRef.current = resolve;
      });
    },
    [],
  );

  const answerConfirm = React.useCallback((val: boolean) => {
    setConfirmOpen(false);
    confirmResolveRef.current?.(val);
    confirmResolveRef.current = null;
  }, []);

  // Convenience for the specific “lose progress?” prompt
  const confirmLoseProgress = React.useCallback(
    () =>
      askConfirm(
        <>{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.LEAVING_PAGE_WARNING')}</>,
        { title: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.LEAVING_PAGE_Q'), destructive: true, confirmText: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.LEAVING_PAGE_LEAVE'), cancelText: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.ALERTS.LEAVING_PAGE_STAY') },
      ),
    [ askConfirm ],
  );


  const handleTogglePanel = async (panel: Panel) => {
    const closingCurrent = openPanel === panel;

    const leavingDirty =
    (openPanel === 'students' && showStudentUploader && studentsDirty) ||
    (openPanel === 'teachers' && showTeacherUploader && teachersDirty) ||
    (openPanel === 'parents' && showParentUploader && parentsDirty);

    if (closingCurrent) {
      if (leavingDirty) {
        const ok = await confirmLoseProgress();
        if (!ok) return;
      }
      setOpenPanel(null);
      return;
    }

    if (leavingDirty) {
      const ok = await confirmLoseProgress();
      if (!ok) return;
    }

    setOpenPanel(panel);

  };

  return (
    <div className="bulk-upload-page">
      <BulkUploadOverrides />
      <RosterManagementLayout >

        <FullBleed>
          <div className="mx-auto w-full max-w-screen-2xl px-4">
            {successErrorModal}
            <div className="flex justify-center mt-4 mb-6">
              <img
                src={whizzyIcon}
                alt="Whizzy"
                className="w-28 h-auto md:w-36"
              />
            </div>
          </div>

          {/* Top notice */}

          <Section className="mb-4 text-center whitespace-pre-line">
            <div className="px-6 py-4 md:px-8 md:py-6 text-center whitespace-pre-line">
              {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.GUIDELINES.HEAD')}
            </div>
          </Section>

          {/* STUDENTS lozenge */}
          <FullBleed >
            <Section className="mb-4" ref={studentsRef} >
              <SectionHeader
                $open={openPanel === 'students'}
                onClick={() => handleTogglePanel('students')}
                aria-expanded={openPanel === 'students'}
              >
                <Title >
                  <span className="pill">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.STUDENTS')}</span>
                  <span className="sub">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.STUDENTS_HINT')}</span>
                  <img src={studentsIcon} alt="Whizzy" className="w-28 h-auto md:w-28" />
                </Title>
                <span style={{ transform: openPanel === 'students' ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }}>
                  ▾
                </span>
              </SectionHeader>

              {openPanel === 'students' && (
                <SectionBody>
                  {!showStudentUploader ? (
                    // ───── Guidelines (shown first) ─────
                    <div className="w-full border-2 border-info rounded-default">
                      <Guidelines className="m-md space-y-12">
                        {/* Step 1 */}
                        <BulletSection
                          number={1}
                          title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.DOWNLOAD')}
                        >
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">

                              <Button
                                className="w-48"
                                color="positive"
                                isOutline
                                icon={GLOBAL_ICONS.download}
                                disabled={isClever}
                                onClick={() => downloadBlankStudentCsv(translate)}
                              >
                                {translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.BLANK')}
                              </Button>

                              <p>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.BLANK_HINT')}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <ExportStudents />
                              <p>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.EXISTING_HINT')}</p>
                            </div>
                          </div>
                        </BulletSection>

                        {/* Step 2 — keep your existing table block here */}
                        <BulletSection
                          number={2}
                          title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.UPDATE')}
                          fullWidth
                        >
                          <div className="space-y-6">
                            <ul className="space-y-2 list-disc pl-10">
                              <li>
                                <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.UPN_WITH_HINT'} />
                              </li>
                              <li>
                                <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.PASSWORD_WITH_HINT'} />
                              </li>
                              <li>
                                <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.ASSESSMENT_DELTA_HINT'} />
                              </li>
                              <li>
                                <TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.TAGS_WITH_HINT'} />
                              </li>
                            </ul>

                            <div className="w-full relative overflow-x-auto px-10">
                              <table className="w-full">
                                <thead>
                                  <tr>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.UPN'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.FIRST_NAME'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LAST_NAME'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.DOB'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.YEAR_GROUP'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.GENDER'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.PASSWORD'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.USERNAME'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.LICENSE'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.CLASS'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.TAG'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ABOVE_BELOW'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.STUDENTS.ASSESSMENT_DELTA'}/></th>

                                  </tr>
                                </thead>
                                <tbody>
                                  {studentExampleRowsV2.map((r) => (
                                    <tr key={r.upn}>
                                      <td>{r.upn}</td>
                                      <td>{r.firstName}</td>
                                      <td>{r.lastName}</td>
                                      <td>{r.dob}</td>
                                      <td>{r.yearGroup}</td>
                                      <td>{r.gender}</td>
                                      <td>{r.password}</td>
                                      <td>{r.username}</td>
                                      <td>{r.license}</td>
                                      <td>{r.className}</td>
                                      <td>{r.tag}</td>
                                      <td>{r.aboveBelowAssessmentDelta}</td>
                                      <td>{r.assessmentDelta}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </BulletSection>

                        {/* Step 3 */}
                        <BulletSection
                          number={3}
                          title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.UPLOAD')}
                        >
                          <Button
                            className="w-48"
                            color="positive"
                            isOutline
                            icon={GLOBAL_ICONS.download}
                            onClick={() => setShowStudentUploader(true)}
                          >
                            {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DATA_PROCESSING_BUTTON')}
                          </Button>
                        </BulletSection>

                        {/* Step 4 — keep your existing block here */}
                      </Guidelines>
                    </div>
                  ) : (
                    // ───── Uploader replaces guidelines ─────
                    <>
                      <div className="mb-4">

                        <Button
                          isOutline
                          icon={GLOBAL_ICONS.back}
                          onClick={async () => {
                            if (showStudentUploader && studentsDirty) {
                              const ok = await confirmLoseProgress();
                              if (!ok) return;
                            }
                            setShowStudentUploader(false);
                          }}
                        >
                          {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.BACK_TO_INSTRUCTIONS')}
                        </Button>
                      </div>

                      <StudentUploadPage onDirtyChange={setStudentsDirty} />
                    </>
                  )}
                </SectionBody>


              )}
            </Section>
          </FullBleed>

          {/* TEACHERS lozenge */}
          {canAddOrEditStaff && (
            <FullBleed>
              <Section className="mb-4" ref={teachersRef}>
                <SectionHeader
                  $open={openPanel === 'teachers'}
                  onClick={() => handleTogglePanel('teachers')}
                  aria-expanded={openPanel === 'teachers'}
                >
                  <Title>
                    <span className="pill">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.TEACHERS')}</span>
                    <span className="sub">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.TEACHERS_HINT')}</span>
                    <img src={techersIcon} alt="Whizzy" className="w-28 h-auto md:w-28" />
                  </Title>
                  <span
                    style={{
                      transform: openPanel === 'teachers' ? 'rotate(180deg)' : 'none',
                      transition: 'transform 120ms',
                    }}
                  >
                  ▾
                  </span>
                </SectionHeader>

                {openPanel === 'teachers' && (
                  <SectionBody>
                    {!showTeacherUploader ? (
                    // ── Guidelines (like Students) ──
                      <div className="w-full border-2 border-info rounded-default">
                        <Guidelines className="m-md space-y-12">
                          {/* Step 1 */}
                          <BulletSection
                            number={1}
                            title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.DOWNLOAD')}
                          >
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Button
                                  className="w-48"
                                  color="positive"
                                  isOutline
                                  icon={GLOBAL_ICONS.download}
                                  disabled={isClever}
                                  onClick={() => downloadBlankTeacherCsv(translate)}
                                >
                                  {translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.BLANK')}
                                </Button>
                                <p>{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.TEACHERS_TEMPLATE_BUTTON_HINT')}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 mt-2">
                              <ExportTeachers />
                              <p>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.EXISTING_STAFF_HINT')}</p>
                            </div>
                          </BulletSection>


                          {/* Step 2 */}
                          <BulletSection number={2} title={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.TEACHERS_STEP2')} fullWidth>
                            <div className="space-y-6">
                              <ul className="space-y-2 list-disc pl-10">
                                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TEACHERS.GUIDE1'} /></li>
                                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TEACHERS.GUIDE2'} /></li>
                                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TEACHERS.GUIDE3'} /></li>
                                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TEACHERS.GUIDE4'} /></li>
                                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TEACHERS.GUIDE5'} /></li>

                              </ul>

                              <div className="w-full relative overflow-x-auto px-10">
                                <table className="w-full">
                                  <thead>
                                    <tr>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.TITLE'}/></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.JOB_TYPE'}/></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.FIRST_NAME'}/></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.LAST_NAME'}/></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.EMAIL'}/></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.RECOVERY_EMAIL'}/></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.PHONE'}/></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.CLASSES'}/></th>
                                      <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.TEACHERS.ADMIN'}/></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {teacherExampleRows.map((r, i) => (
                                      <tr key={i}>
                                        <td>{r.title}</td>
                                        <td>{r.jobtype}</td>
                                        <td>{r.firstName}</td>
                                        <td>{r.lastName}</td>
                                        <td>{r.email}</td>
                                        <td>{r.recoveryEmail}</td>
                                        <td>{r.phone}</td>
                                        <td>{r.classes}</td>
                                        <td>{r.admin}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </BulletSection>


                          {/* Step 3 */}
                          <BulletSection number={3} title={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.UPLOAD')}>
                            <Button
                              className="w-48"
                              color="positive"
                              isOutline
                              icon={GLOBAL_ICONS.download}
                              onClick={() => setShowTeacherUploader(true)}
                            >
                              {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DATA_PROCESSING_BUTTON')}
                            </Button>
                          </BulletSection>

                        </Guidelines>
                      </div>
                    ) : (
                    // ── Embedded uploader (Teachers tab locked) ──
                      <>
                        <div className="mb-4">
                          <Button
                            isOutline
                            icon={GLOBAL_ICONS.back}
                            onClick={async () => {
                              if (showTeacherUploader && teachersDirty) {
                                const ok = await confirmLoseProgress();
                                if (!ok) return;
                              }
                              setShowTeacherUploader(false);
                            }}
                          >
                            {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.BACK_TO_INSTRUCTIONS')}
                          </Button>
                        </div>


                        {/* Reuse your uploader with Teachers tab active */}
                        <StudentUploadPage initialTab="teachers" lockTab onDirtyChange={setTeachersDirty} />
                      </>
                    )}
                  </SectionBody>
                )}
              </Section>
            </FullBleed>
          )}

          {/* PARENTS lozenge */}
          <FullBleed>
            <Section ref={parentsRef}>
              <SectionHeader
                $open={openPanel === 'parents'}
                onClick={() => handleTogglePanel('parents')}
                aria-expanded={openPanel === 'parents'}
              >
                <Title>
                  <span className="pill">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.PARENTS')}</span>
                  <span className="sub">{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.PARENTS_HINT')}</span>
                  <img src={parentsIcon} alt="Whizzy" className="w-10 h-auto md:w-10" />
                </Title>
                <span style={{ transform: openPanel === 'parents' ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }}>
                  ▾
                </span>
              </SectionHeader>

              {openPanel === 'parents' && (
                <SectionBody>
                  {!showParentUploader ? (
                    // ───── Parents guidelines ─────
                    <div className="w-full border-2 border-info rounded-default">
                      <Guidelines className="m-md space-y-12">
                        <BulletSection number={1} title={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.STEP1')}>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Button
                                className="w-48"
                                color="positive"
                                isOutline
                                icon={GLOBAL_ICONS.download}
                                onClick={() => downloadBlankParentsCsv(translate)}
                              >
                                {translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.BLANK')}
                              </Button>
                              <p>{translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.GUIDELINES.PARENTS_TEMPLATE_BUTTON_HINT')}</p>
                            </div>
                          </div>
                        </BulletSection>

                        <BulletSection number={2} title={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.STEP2')} fullWidth>
                          <div className="space-y-6">
                            <ul className="space-y-2 list-disc pl-10">
                              <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.GUIDE1'}/></li>
                              <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.GUIDE2'}/></li>
                              <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.GUIDE3'}/></li>
                              <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.PARENTS.GUIDE4'}/></li>
                            </ul>

                            <div className="w-full relative overflow-x-auto px-10">
                              <table className="w-full">
                                <thead>
                                  <tr>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.UPN'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.STUDENT_NAME'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_NAME'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G1_EMAIL'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_NAME'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G2_EMAIL'}/> </th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_NAME'}/></th>
                                    <th><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.BULK_UPLOADS.TABLES.PARENTS.G3_EMAIL'}/> </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {parentExampleRows.map((r, i) => (
                                    <tr key={i}>
                                      <td>{r.upn}</td>
                                      <td>{r.studentName}</td>
                                      <td>{r.g1Name}</td>
                                      <td>{r.e1}</td>
                                      <td>{r.g2Name}</td>
                                      <td>{r.e2}</td>
                                      <td>{r.g3Name}</td>
                                      <td>{r.e3}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </BulletSection>


                        <BulletSection number={3} title={translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.LOZANGES.UPLOAD')}>
                          <Button
                            className="w-48"
                            color="positive"
                            isOutline
                            icon={GLOBAL_ICONS.download}
                            onClick={() => setShowParentUploader(true)}
                          >
                            {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.DATA_PROCESSING_BUTTON')}
                          </Button>
                        </BulletSection>

                      </Guidelines>
                    </div>
                  ) : (
                    // ───── Embedded uploader (replaces guidelines) ─────
                    <>
                      <div className="mb-4">
                        <Button
                          isOutline
                          icon={GLOBAL_ICONS.back}
                          onClick={async () => {
                            if (showParentUploader && parentsDirty) {
                              const ok = await confirmLoseProgress();
                              if (!ok) return;
                            }
                            setShowParentUploader(false);
                          }}
                        >
                          {translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.BACK_TO_INSTRUCTIONS')}
                        </Button>
                      </div>

                      {/* Lock to the Parents tab */}
                      <StudentUploadPage initialTab="parents" lockTab onDirtyChange={setParentsDirty} />
                    </>
                  )}
                </SectionBody>
              )}

            </Section>
          </FullBleed>

        </FullBleed>

        {confirmOpen && (
          <Modal
            open
            toggle={(v?: boolean) => setConfirmOpen(Boolean(v))}
            onClose={() => answerConfirm(false)}
            disableMaskClick
            alwaysOnTop
            size="md"
          >
            <div className="p-4">
              {confirmTitle && <h3 className="text-lg font-semibold mb-2">{confirmTitle}</h3>}
              <div className="mb-4">{confirmBody}</div>
              <ModalFooter justify="end">
                {/* Stay button → green outline (like Add Row) */}
                <Button
                  data-id="whizz_button"
                  color="positive"
                  isOutline
                  className="!h-8 !py-0 !px-3 mr-2"
                  onClick={() => answerConfirm(false)}
                >
                  {confirmCancelText}
                </Button>

                {/* Leave button → red outline */}
                <Button
                  data-id="whizz_button"
                  color="negative"
                  isOutline
                  className="!h-8 !py-0 !px-3"
                  onClick={() => answerConfirm(true)}
                >
                  {confirmOkText}
                </Button>
              </ModalFooter>
            </div>
          </Modal>
        )}

      </RosterManagementLayout>
    </div>
  );
};

const BulletSection = ({
  number,
  title,
  fullWidth = false,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) => (
  <div className={'grid items-center'} style={{ gridTemplateColumns: '60px auto' }}>
    <h2 className={'bg-positive rounded-full p-md text-xl mr-md w-12 h-12 flex justify-center items-center font-bold'}>
      {number}
    </h2>
    <h2 className={'text-2xl font-bold text-positive'}>{title}</h2>
    <div className={`mt-md ${fullWidth ? 'col-span-2' : 'col-start-2'}`}>{children}</div>
  </div>
);

// CSV export helpers

const ExportStudents = () => {
  const { translate } = useI18n();
  const [ isClever ] = useCleverIntegration();
  const [ isDownloading, setIsDownloading ] = React.useState(false);
  const { me } = useIdentity();
  const localeId = (
    (me as unknown as { locale?: { id?: string | null } })?.locale?.id ??
  'en-gb'
  ).toLowerCase();
  const isMonthFirstDobLocale = localeId === 'en-us' || localeId === 'en-nz';


  const download = async () => {
    if (isClever || isDownloading) return;
    setIsDownloading(true);
    try {
      // 1) Fetch students
      const students = await repository.StudentRepository.rosterManagementListItemsByAction(
        RosterManagementStudentActionEnum.edit,
      );

      // 2) Work out schoolId for year groups (try students first, then classes)
      const schoolId: number | undefined =
        (students?.[0] as any)?.schoolId ??
        (await (async () => {
          try {
            const cls = await repository.NewClassRepository.getClasses();
            return cls?.[0]?.schoolId;
          } catch {
            return undefined;
          }
        })());

      // 3) Fetch year groups and build a map (id -> code)
      let ygById = new Map<number, string>();
      if (schoolId) {
        try {
          const yearGroups = await repository.YearGroupRepository.allBySchoolId(schoolId);
          ygById = new Map(
            yearGroups.map((yg: any) => [ (yg.yearGroupId ?? yg.id), String(yg.code ?? '').trim() ]),
          );
        } catch {
          // ignore — fall back to blank codes below
        }
      }

      // 4) Build CSV rows
      const rows = sortBy(students, [ 'lastName' ]).map((v: RosterManagementStudentListItem) => {

        const dob = v.dob
          ? DateTime.fromISO(v.dob).toFormat(
            isMonthFirstDobLocale ? 'LL/dd/yyyy' : 'dd/LL/yyyy',
          )
          : '';
        const ygCode = v.yearGroupId ? (ygById.get(v.yearGroupId) ?? '') : '';

        const gAbbr = (genders?.[v.gender]?.abbreviation ?? '').toUpperCase();
        const gender = gAbbr.charAt(0);

        const username = (v as any).username ?? (v as any).loginName ?? '';
        const classNames = Array.from(
          new Set(
            (v.classes ?? [])
              .map((c: any) => String(c?.name ?? '').trim())
              .filter(Boolean),
          ),
        ).join(', ');

        const tags = Array.from(
          new Set(
            (v.tags ?? [])
              .map((t: any) => String(t?.abbreviation ?? '').trim())
              .filter(Boolean),
          ),
        ).join(',');

        return {
          'Archive': 'N',
          'UPN': v.upn ?? '',
          'First Name': v.firstName ?? '',
          'Last Name': v.lastName ?? '',
          'Date Of Birth': dob,
          'Year Group': ygCode,
          'Gender': gender,
          'Password': '',
          'Username': username,
          'Activate Student': v.active ? 'Y' : '',
          'Class': classNames,
          'Tags': tags,
          'Working Age': '',
          'Assessment Age': '',
        };
      });

      // 5) Export
      exportFromJson({
        data: rows,
        fileName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_STUDENTS_FILENAME') || 'Student_export',
        exportType: 'csv',
        withBOM: true,
        fields: STUDENT_EXPORT_HEADERS,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      className="w-48"
      color="positive"
      isOutline
      icon={GLOBAL_ICONS.download}
      disabled={isClever || isDownloading}
      onClick={download}
    >
      {translate(
        `pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.${isDownloading ? 'DOWNLOADING' : 'EXISTING'}`,
      )}
    </Button>
  );
};

const ExportTeachers = () => {
  const { translate } = useI18n();
  const [ isDownloading, setIsDownloading ] = React.useState(false);

  const download = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      // 1) get all staff (we only need teachers, but we’ll use classes to resolve names)
      const staff = await (repository as any).StaffRepository.getStaff();

      const classes: Array<{ id: number; name: string }> = staff?.classes ?? [];
      const teachers: Array<{
        firstName?: string;
        lastName?: string;
        email?: string | null;
        phone?: string | null;
        isAdmin?: number | boolean;
        jobType?: string | number | null;
        classesIds?: number[];
      }> = staff?.teachers ?? [];

      // 2) build classId -> name lookup
      const classNameById = new Map<number, string>(
        classes.map((c) => [ Number(c.id), String(c.name ?? '').trim() ]),
      );

      // 3) rows in the exact template order
      const rows = teachers.map((t) => {
        const classNames = (t.classesIds ?? [])
          .map((id) => classNameById.get(Number(id)) || '')
          .filter(Boolean)
          .join(',');

        // `Title` (honorific) doesn’t exist on this endpoint; leave blank.
        // `Job Type` in the uploader is free text; default to "Teacher" when missing.
        const jobTypeText =
          typeof t.jobType === 'string' && t.jobType.trim()
            ? t.jobType
            : t.jobType != null && t.jobType !== ''
              ? String(t.jobType)
              : 'Teacher';

        return {
          'Job Type': jobTypeText,
          'Title': '',
          'First Name': t.firstName ?? '',
          'Last Name': t.lastName ?? '',
          'Email Address': t.email ?? '',
          'Recovery Email Address': '',
          'Phone': t.phone ?? '',
          'Classes': classNames,
          'Admin': t.isAdmin ? 'Y' : '',
        };
      });

      exportFromJson({
        data: rows,
        fileName: translate('pages.ROSTER_MANAGEMENT.BULK_UPLOADS.COMMON.EXPORT_TEACHERS_FILENAME') || 'teachers_export',
        exportType: 'csv',
        withBOM: true,
        fields: TEACHER_TEMPLATE_HEADERS,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      className="w-48"
      color="positive"
      isOutline
      icon={GLOBAL_ICONS.download}
      onClick={download}
      disabled={isDownloading}
    >
      {translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.EXISTING_STAFF')}
    </Button>
  );
};




export default Upload2;
